import { Flex, MultiSelectComboBox, Select, TagBadge, Text, type SelectOption } from '@chatsdet/ui';
import type { TicketTaxonomy } from '../../../hooks/useTicketingProviders';

const NO_TYPE_KEY = '__no_type__';

/**
 * Type and labels pickers driven by a provider's taxonomy. `cofactor` is always added by the
 * server, so it is shown locked rather than offered. Used by the Tickets page defaults and the
 * per-ticket filing options.
 */
export function FilingTaxonomyFields({
  taxonomy,
  type,
  labels,
  onTypeChange,
  onLabelsChange,
  isDisabled = false,
}: {
  taxonomy: TicketTaxonomy | undefined;
  type: string | null;
  labels: string[];
  onTypeChange: (type: string | null) => void;
  onLabelsChange: (labels: string[]) => void;
  isDisabled?: boolean;
}) {
  const typeOptions: SelectOption[] = [
    { id: NO_TYPE_KEY, label: 'No type' },
    ...(taxonomy?.types ?? []).map((entry) => ({
      id: entry.name,
      label: entry.name,
      description: entry.description || undefined,
    })),
  ];
  const labelItems = (taxonomy?.labels ?? []).map((entry) => ({
    id: entry.name,
    label: entry.name,
    description: entry.description || undefined,
  }));
  const typesUnavailable = taxonomy !== undefined && taxonomy.types === null;
  const labelsUnavailable = taxonomy !== undefined && taxonomy.labels === null;

  return (
    <Flex direction="column" gap="md">
      <Select
        label="Type"
        selectedKey={type ?? NO_TYPE_KEY}
        items={typeOptions}
        isDisabled={isDisabled || taxonomy === undefined || typesUnavailable}
        description={typesUnavailable ? taxonomy?.notes.join(' ') : undefined}
        onSelectionChange={(key) => onTypeChange(key === NO_TYPE_KEY ? null : String(key))}
      />
      <Flex direction="column" gap="xs">
        <MultiSelectComboBox
          label="Labels"
          placeholder="Add labels..."
          items={labelItems}
          value={labels.map((name) => ({ id: name, label: name }))}
          isDisabled={isDisabled || taxonomy === undefined || labelsUnavailable}
          description={labelsUnavailable ? taxonomy?.notes.join(' ') : undefined}
          onChange={(selected) => onLabelsChange(selected.map((item) => item.id))}
        />
        <Flex gap="xs" align="center">
          <TagBadge name="cofactor" color="gray" hideDot size="sm" />
          <Text size="sm" className="text-muted-foreground">
            Always added to tickets Cofactor files
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
